import { FC, useCallback, useEffect, useState } from "react";
import { FilterCustomer, FindCustomerModal } from "#/organisms";
import { useCustomer, useFilter, useModal } from "@/hooks";

import { CustomerAndPackage } from "#/pages";
import { GetListParams } from "@/api/customer/interface";
import { useApi } from "@/api";
import { useNavigate } from "react-router-dom";

interface CustomerAndPackageViewProps {
  isAdmin: boolean;
  selectOrderTemplate: (branchId: number,  orderTypeId: number) => void;
}

const CustomerAndPackageView: FC<CustomerAndPackageViewProps> = ({
  isAdmin,
  selectOrderTemplate
}) => {
  const { customer, orderTemplate, order } = useApi();

  const {
    filter,
    numberOfFilters,
    setShouldFilter,
    shouldFilter,
    ...filterProps
  } = useFilter<FilterCustomer>();

  const { selectedCustomer, setSelectedCustomer } = useCustomer();

  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingOrderTemplates, setLoadingOrderTemplates] = useState(false);
  const { showModal, onCloseModal, onOpenModal } = useModal();
  const [currentPage, setcurrentPage] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    setSelectedCustomer(null);
    order.clearOrderDetails();
    orderTemplate.clearOrderTemplates();
  }, []);

  const getCustomers = async (data?: GetListParams) => {
    setLoadingCustomers(true);
    await customer.getList(data);
    setLoadingCustomers(false);
  }

  const getOrderTemplates = async (customerId: string) => {
    setLoadingOrderTemplates(true);
    const branches = await customer.getBranches(customerId);
    orderTemplate.getList(branches);
    setLoadingOrderTemplates(false);
  }

  useEffect(() => {
    if (shouldFilter) {
      if (filter) {
        getCustomers({
          accountName: filter.login,
          address: filter.location,
          code: filter.customerCode,
          email: filter.customerEmail,
          isActive: filter.active,
          name: filter.companyName,
          page: currentPage
        });
      } else {
        getCustomers();
      }
      setShouldFilter(false);
    }
  }, [filter, shouldFilter]);

  useEffect(() => {
    if (selectedCustomer) {
      getOrderTemplates(selectedCustomer.id);
    }
  }, [selectedCustomer]);

  const handleShowMore = useCallback(() => {
    setcurrentPage(currentPage + 1);
  }, []);

  const handleCreateCustomer = useCallback(() => {
    navigate('/customer-create');
  }, []);

  const handleFindCustomerModal = useCallback(() => {
    if (customer.customers.length === 0) {
      getCustomers();
    }
    onOpenModal();
  }, [customer.customers]);

  const handleSelectCustomer = useCallback((customerId: string) => {
    const selected = customer.customers.find((item) => item.id === customerId);
    
    if (selected) {
      setSelectedCustomer(selected);
    }
  }, [customer.customers]);

  const handleRemoveCustomer = useCallback(() => {
    setSelectedCustomer(null);
  }, []);

  const handleSelectOrderTemplate = useCallback((orderTypeId: number) => {
    selectOrderTemplate(selectedCustomer?.branchId || 0, orderTypeId);
  }, [selectedCustomer]);

  return (
    <>
      <CustomerAndPackage
        isAdmin={isAdmin}
        loadingOrderTemplates={loadingOrderTemplates}
        onCreateCustomer={handleCreateCustomer}
        onFindCustomer={handleFindCustomerModal}
        onSelectOrderTemplate={handleSelectOrderTemplate}
        onRemoveCustomer={handleRemoveCustomer}
        orderTemplates={orderTemplate.orderTemplates}
        selectedCustomer={selectedCustomer}
        onShowMore={handleShowMore}
      />
      {showModal && (
        <FindCustomerModal
          customers={customer.customers}
          loading={loadingCustomers}
          onClose={onCloseModal}
          onSelectCustomer={handleSelectCustomer}
          {...filterProps}
          filter={filter}
          numberOfFilters={numberOfFilters}
        />
      )}
    </>
  );
}

export default CustomerAndPackageView;